import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Vote, VoteCreate, VoteDetail, VoteFilter, VoteUpdate } from '@core/types/vote.type';

@Injectable({ providedIn: 'root' })
export class VoteService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/votes`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Vote>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as VoteFilter;
    if (filter.voteType !== undefined) params = params.set('filter.voteType', String(filter.voteType));
    if (filter.status !== undefined) params = params.set('filter.status', String(filter.status));
    if (filter.fromDate) params = params.set('filter.fromDate', filter.fromDate);
    if (filter.toDate) params = params.set('filter.toDate', filter.toDate);

    return this.http.get<ApiResponse<PaginationResult<Vote>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading votes')),
    );
  }

  get(id: string): Observable<ApiResponse<VoteDetail>> {
    return this.http.get<ApiResponse<VoteDetail>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading vote')),
    );
  }

  create(data: VoteCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating vote')),
    );
  }

  update(data: VoteUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating vote')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting vote')),
    );
  }
}

